import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import ExamService from '../../services/exam.service';
import toast from 'react-hot-toast';

interface IUploadUserSheetModal {
  show: boolean;
  handleClose: () => void;
}

const UploadUserSheetModal = ({ show, handleClose }: IUploadUserSheetModal) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const onFileUpload = async (file: File) => {
    if (!file) {
      toast.error('Please select a CSV file before uploading.');
      return;
    }

    const formData = new FormData();
    formData.append('students', file);

    try {
      setUploading(true);
      const res = await ExamService.uploadReverficationSheet(show, formData);
      if (res.status === 200) {
        toast.success('File uploaded successfully');
        setSelectedFile(null);
        handleClose();
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data ||
          'Failed to upload file'
      );
    } finally {
      setUploading(false);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length) {
        const file = acceptedFiles[0];
        setSelectedFile(file);
        onFileUpload(file);
      }
    },
    [show] // ensures updated context of `show` for API
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    multiple: false,
  });

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false}>
      <Modal.Body>
        <div
          {...getRootProps()}
          className={`border border-2 rounded p-4 text-center ${isDragActive ? 'bg-light' : ''}`}
          style={{ cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}
        >
          <input {...getInputProps()} disabled={uploading} />
          <FontAwesomeIcon icon={faUpload} size="3x" className="mb-3 text-primary" />
          <p className="fw-bold">
            {uploading ? 'Uploading file...' : 'Drag and drop a file here, or click to select'}
          </p>
          <small className="text-muted">Supported format: .csv</small>

          {selectedFile && (
            <div className="mt-3">
              <FontAwesomeIcon icon={faFileAlt} className="me-2 text-success" />
              <span className="text-success">{selectedFile.name}</span>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={uploading}>
          {uploading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Uploading...
            </>
          ) : (
            'Close'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadUserSheetModal;
